import React from 'react';
import { Tooltip } from '@jjj/components';
import { generateFulfilledCell } from '../../../components/tables/fulfilledCell';
import {
  CurrencyCell,
  TextCell,
  TextCellWithTag,
  AncestorsCell,
  TooltipCellWithHover,
} from '../../../components/tables/helperCells';
import { getFormattedTime } from '../../../helpers/dateTime';
import { getJobFormattedDate, loadMoreJobsButton as LoadMoreJobsButton, getJobStatusTitle } from './utils';
import { t } from '../../../i18n';
import { getActionColumn } from '../../../components/tables/helperColumns';
import {
  FirstNameCharacter,
  IconRowContainer,
  IconWrapper,
  IconsBadgeWrapper,
  IconsContainer,
  MaPauseStatusWrapper,
  PlaceHolderContainer,
  TooltipIconsContainer,
  VisibleIconsContainer,
} from './columns.styles';
import defaultAccreditationLogo from '../../../assets/svg/default-accreditation.svg';
import { formatStreet } from '../../../helpers/address';

export function getCompanyNameColumn(data = { maPauseStatus: false }) {
  return {
    title: t('dash.jobs.jobColumns.company_name_title'),
    key: 'company_name',
    sorter: true,
    truncated: true,
    getContentLength: object => object?.company?.name?.length || 0,
    render: (object, url) => {
      let companyName = object?.company?.name || '';

      if (data.maPauseStatus && object?.ma_paused_by_any_reason) {
        companyName = (
          <MaPauseStatusWrapper>
            <Tooltip title={t('dash.jobs.jobColumns.ma_pause_status_tooltip')}>
              <i className="far fa-pause-circle ma-pause-icon" />
            </Tooltip>
            {companyName}
          </MaPauseStatusWrapper>
        );
      }

      return AncestorsCell(companyName, object?.company?.ancestors, url);
    },
    ...data,
  };
}

export function getJobAltNameColumn(data = {}) {
  return {
    title: t('dash.jobs.jobColumns.alt_name_title'),
    key: 'name',
    sorter: false,
    truncated: true,
    getContentLength: object => object?.name?.length || 0,
    render: (object, url) => TextCell(object?.name || '', url),
    ...data,
  };
}

export function getCompanyNameAddressColumn(data = {}) {
  return {
    title: t('dash.jobs.jobColumns.client_title'),
    key: 'company_name',
    sorter: false,
    truncated: true,
    getContentLength: object => object?.company?.name?.length || 0,
    render: (object, url) => {
      if (object.isLoadMoreRow || object.isChildJob) {
        return null;
      }
      const { company } = object;

      return TooltipCellWithHover(
        company?.name,
        object.address,
        company?.ancestors?.length && company.ancestors.join('  \u25B8  '),
        url,
      );
    },
    ...data,
  };
}

export function getDateColumn(data = {}) {
  return {
    title: t('dash.jobs.jobColumns.date_title'),
    key: 'date',
    sorter: true,
    render: (object, url) => {
      const tagText = object.children ? t('dash.jobs.jobColumns.date_tag') : null;

      return TextCellWithTag(getJobFormattedDate(object), url, tagText);
    },
    ...data,
  };
}

export function getStartsAtColumn(data = {}) {
  return {
    title: t('dash.jobs.jobColumns.starts_at_title'),
    key: 'starts_at',
    sorter: true,
    render: (object, url) =>
      TextCell(getFormattedTime({ date: object.starts_at, formatKey: 'time_hours_minutes_long' }), url),
    ...data,
  };
}

export function getAddressColumn(data = {}) {
  return {
    title: t('dash.jobs.jobColumns.location_title'),
    key: 'address',
    sorter: false,
    truncated: true,
    getContentLength: object => object.address?.length || 0,
    loadMoreButton: object => <LoadMoreJobsButton onClick={() => this.handleLoadMore(object)} />,
    render: (object, url) => TextCell(formatStreet(object.address), url),
    ...data,
  };
}

export function getFulfilledColumn(data = {}) {
  return {
    title: t('dash.jobs.jobColumns.fulfilled_title'),
    key: 'fulfilled',
    sorter: false,
    render: (object, url) => generateFulfilledCell(object, url),
    ...data,
  };
}

export function getGroupedIcons(items = [], dataKey = 'logo') {
  const showTooltip = items.length > 2;

  const visibleIconsItems = items.slice(0, 2);
  let iconsInTooltip = items.slice(2, items.length);

  let visibleIcons = showTooltip && <IconsBadgeWrapper>+{items.length - 2}</IconsBadgeWrapper>;

  iconsInTooltip = (
    <TooltipIconsContainer>
      {iconsInTooltip.map((item, index) => (
        <IconRowContainer key={item?.id || item?.icon || index}>
          <IconWrapper $url={item[dataKey]} />
          {item.name && <span className="icons-tooltip-agency-name">{item?.name}</span>}
        </IconRowContainer>
      ))}
    </TooltipIconsContainer>
  );

  const getAccreditationIcon = (imageUrl, accreditationName) => {
    if (!imageUrl) {
      return (
        <PlaceHolderContainer>
          <FirstNameCharacter>{accreditationName?.charAt(0)?.toUpperCase()}</FirstNameCharacter>

          <IconWrapper $url={defaultAccreditationLogo} />
        </PlaceHolderContainer>
      );
    }

    return <IconWrapper $url={imageUrl} />;
  };

  visibleIcons = (
    <IconsContainer>
      <VisibleIconsContainer>
        {visibleIconsItems.map((item, index) => (
          <Tooltip title={item?.name} key={item?.id || item?.icon || index} placement="bottom">
            {getAccreditationIcon(item[dataKey], item?.name)}
          </Tooltip>
        ))}
      </VisibleIconsContainer>
      {showTooltip && <Tooltip title={iconsInTooltip}>{visibleIcons}</Tooltip>}
    </IconsContainer>
  );

  return visibleIcons;
}

export function getPoolsColumn(data = {}) {
  return {
    title: t('dash.jobs.jobColumns.pool_title'),
    key: 'pool',
    sorter: false,
    render: (object, url) => {
      return TextCell(getGroupedIcons(object?.pool_organizations), url);
    },
    ...data,
  };
}

export function getBillColumn(data = {}) {
  const currentOrganizationId = this.getCurrentOrganizationId();

  return {
    title: t('dash.jobs.jobColumns.est_bill_title'),
    key: 'bill',
    sorter: false,
    render: (object, url) => {
      const { company, workers_assigned, bill } = object;

      const info =
        company &&
        workers_assigned &&
        currentOrganizationId !== company.organization.id &&
        t('dash.jobs.custom_pool_pricing_warning');

      return CurrencyCell(bill, url, info);
    },
    ...data,
  };
}

export function getStatusColumn(data = {}) {
  return {
    title: t('dash.jobs.jobColumns.status_title'),
    key: 'status',
    sorter: false,
    render: (object, url) => TextCell(getJobStatusTitle(object), url),
    ...data,
  };
}

export function getUnactionedJobInvitesColumn(data = {}) {
  return {
    title: t('dash.jobs.jobColumns.unactioned_job_invites_count_title'),
    key: 'unactioned_job_invites_count',
    sorter: false,
    render: (object, url) => TextCell(object.unactioned_job_invites_count || 0, url),
    ...data,
  };
}

export function getShiftsUnfilledColumn(data = {}) {
  return {
    title: t('dash.jobs.jobColumns.shifts_unfilled_title'),
    key: 'shifts_unfilled',
    sorter: false,
    render: (object, url) => TextCell(object.shifts_unfilled || 0, url),
    ...data,
  };
}

export function getActionsColumn(data = {}) {
  return getActionColumn(object => (this.popoverContent ? this.popoverContent(object) : null), data);
}
